<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'

const props = defineProps({
	modelValue: {
		default: '',
	},
	options: {
		type: Array,
		default: () => [],
	},
	size: {
		type: String,
		default: 'md',
	},
	id: {
		type: String,
	},
	label: {
		type: String,
	},
	required: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
})

const emit = defineEmits(['update:modelValue', 'change'])

const value = computed({
	get: () => props.modelValue,
	set: (v) => emit('update:modelValue', v),
})

function normalizeOption(option) {
	if (typeof option === 'object' && option !== null) {
		return {
			value: option.value,
			text: option.text ?? option.label ?? String(option.value),
		}
	}
	return { value: option, text: String(option) }
}

const normalizedOptions = computed(() => props.options.map(normalizeOption))

// Under md the native select keeps 16px text so iOS Safari does not zoom when
// the picker opens — but that is 3px bigger than every input placeholder, button
// and label around it (Trello Wfx0KqvP). There, the select's own text is made
// transparent and a 13px label drawn over it shows the choice; the select still
// takes the tap, the focus ring and the picker. From md up nothing is overlaid.
const narrow = ref(false)
let narrowMql = null
const syncNarrow = () => { narrow.value = !!narrowMql?.matches }
onMounted(() => {
	if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
	narrowMql = window.matchMedia('(max-width: 767px)')
	syncNarrow()
	narrowMql.addEventListener('change', syncNarrow)
})
onBeforeUnmount(() => narrowMql?.removeEventListener('change', syncNarrow))

const selectedText = computed(() => {
	const v = props.modelValue
	const opts = normalizedOptions.value
	const hit = opts.find((o) => o.value === v) ?? opts.find((o) => String(o.value) === String(v))
	return (hit ?? opts[0])?.text ?? ''
})

// `lg` has no 16px/13px switch, so it never needs the overlay.
const overlaid = computed(() => narrow.value && props.size !== 'lg')
const sizeClass = computed(() => (props.size === 'lg' ? 'h-10 pl-3 pr-9' : props.size === 'sm' ? 'h-7 pl-2 pr-8' : 'h-8 pl-3 pr-9'))
const selectClass = computed(() => {
	// Native chevrons sit flush against the right edge; draw our own, inset 12px, and keep room for it.
	const base = [
		'form-select-chevron appearance-none w-full leading-body bg-surface-3 focus:bg-surface-4 focus:outline-0 focus:ring-0 rounded transition-color border-neutral-n50 focus:border-surfaces-primary disabled:cursor-not-allowed',
		overlaid.value ? 'text-transparent disabled:text-transparent' : 'text-surface-text-2 disabled:text-surface-7',
		sizeClass.value,
	]
	if (props.size !== 'lg') base.push('text-base md:text-tiny')
	return base.join(' ')
})
const overlayClass = computed(() => [
	'pointer-events-none absolute inset-0 truncate text-tiny',
	sizeClass.value,
	props.size === 'sm' ? 'leading-7' : 'leading-8',
	props.disabled ? 'text-surface-7' : 'text-surface-text-2',
])

function onChange(event) {
	emit('change', event)
}
</script>

<template>
	<div class="relative w-full">
		<label v-if="label" class="mb-2 block font-medium text-tiny text-surface-text-1" :for="id">{{ label }}</label>
		<div class="relative">
			<select
				:id="id"
				v-model="value"
				:class="selectClass"
				:required="required"
				:disabled="disabled"
				@change="onChange"
			>
				<!-- Explicit colour: a desktop-style dropdown list would otherwise inherit the transparent text under md. -->
				<option v-for="option in normalizedOptions" :key="option.value" :value="option.value" class="text-surface-text-2">
					{{ option.text }}
				</option>
			</select>
			<span v-if="overlaid" :class="overlayClass" aria-hidden="true" :data-testid="id ? `${id}-label` : undefined">{{ selectedText }}</span>
		</div>
	</div>
</template>

<style>
/* Chevron in surface-8 (rgb(174 186 214)); `appearance-none` removes the native one. */
.form-select-chevron {
	background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M2.5 4.5 6 8l3.5-3.5' stroke='rgb(174,186,214)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
	background-repeat: no-repeat;
	background-position: right 12px center;
	background-size: 12px 12px;
}
.form-select-chevron:disabled {
	opacity: 0.7;
}
</style>
